import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and active clinic to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('agrosvet_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const activeClinic = localStorage.getItem('agrosvet_active_clinic');
  if (activeClinic) {
    config.headers['X-Clinic-Id'] = activeClinic;
  }
  return config;
});

// Retry logic for network errors (server cold start)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const isNetworkError = !error.response && (error.code === 'ECONNABORTED' || error.message?.includes('Network Error') || error.message?.includes('timeout'));

    // Retry once on network/timeout errors (server waking up)
    if (isNetworkError && !config._retried) {
      config._retried = true;
      await new Promise(r => setTimeout(r, 3000));
      return api(config);
    }

    // Handle 401 errors (skip login endpoints)
    const url = config?.url || '';
    const isLoginRequest = url.includes('/auth/login') || url.includes('/customer/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('agrosvet_token');
      localStorage.removeItem('agrosvet_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (phone, password) => api.post('/auth/login', { phone, password });
export const getMe = () => api.get('/auth/me');
export const updateProfile = (data) => api.put('/auth/profile', data);

// Clinic Staff
export const getClinicStaff = () => api.get('/clinic/staff');
export const createClinicStaff = (data) => api.post('/clinic/staff', data);
export const deleteClinicStaff = (id) => api.delete(`/clinic/staff/${id}`);
export const updateStaffPermissions = (userId, data) => api.put(`/clinic/staff/${userId}/permissions`, data);

// Customers
export const getCustomers = (search, debtorsOnly) => api.get('/customers', { params: { search, debtors_only: debtorsOnly || undefined } });
export const getCustomerStats = () => api.get('/customers/stats');
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);

// Customer Balance
export const getCustomerBalance = (id) => api.get(`/customers/${id}/balance`);
export const addBalanceTransaction = (id, data) => api.post(`/customers/${id}/balance`, data);
export const updateBalanceTransaction = (id, txId, data) => api.put(`/customers/${id}/balance/${txId}`, data);
export const deleteBalanceTransaction = (id, txId) => api.delete(`/customers/${id}/balance/${txId}`);

// Pets
export const getPets = (customerId, search) => api.get('/pets', { params: { customer_id: customerId, search: search || undefined } });
export const getPet = (id) => api.get(`/pets/${id}`);
export const createPet = (data) => api.post('/pets', data);
export const updatePet = (id, data) => api.put(`/pets/${id}`, data);

// Operations
export const getOperations = (params) => api.get('/operations', { params });
export const createOperation = (data) => api.post('/operations', data);
export const createOperationsBatch = (data) => api.post('/operations/batch', data);
export const createQuickSale = (data) => api.post('/quick-sale', data);
export const deleteOperation = (id) => api.delete(`/operations/${id}`);
export const getUpcomingVaccines = (days = 30) => api.get('/operations/upcoming-vaccines', { params: { days } });
export const getTodaysVaccines = () => api.get('/operations/todays-vaccines');
export const getMonthlyVaccines = () => api.get('/operations/monthly-vaccines');

// Quick Add
export const quickAdd = (data) => api.post('/quick-add', data);

// Stats
export const getDailyStats = (date) => api.get('/stats/daily', { params: { date } });
export const getWeeklyStats = (weekStart) => api.get('/stats/weekly', { params: weekStart ? { week_start_date: weekStart } : {} });
export const getMonthlyStats = (month) => api.get('/stats/monthly', { params: month ? { month } : {} });
export const getYearlyStats = (year) => api.get('/stats/yearly', { params: year ? { year } : {} });

// Admin
export const getClinics = () => api.get('/admin/clinics');
export const createClinic = (data) => api.post('/admin/clinics', data);
export const extendSubscription = (clinicId, years = 1) => api.post(`/admin/clinics/${clinicId}/subscription`, { years });
export const updateSmsCredits = (clinicId, amount) => api.post(`/admin/clinics/${clinicId}/sms-credits`, { amount });
export const getMySmsCredits = () => api.get('/clinic/sms-credits');

// Subscription
export const getMySubscription = () => api.get('/clinic/subscription');
export const getSubscriptionPlans = () => api.get('/subscription/plans');
export const getSmsPackages = () => api.get('/subscription/sms-packages');
export const purchaseSubscription = (planId) => api.post('/subscription/purchase', { plan_id: planId });
export const purchaseSmsPackage = (packageId) => api.post('/subscription/purchase-sms', { package_id: packageId });
export const getPurchaseHistory = () => api.get('/subscription/history');

// Admin Subscription Plan Management
export const getAdminSubscriptionPlans = () => api.get('/admin/subscription-plans');
export const createSubscriptionPlan = (data) => api.post('/admin/subscription-plans', data);
export const updateSubscriptionPlan = (id, data) => api.put(`/admin/subscription-plans/${id}`, data);
export const deleteSubscriptionPlan = (id) => api.delete(`/admin/subscription-plans/${id}`);

// Admin SMS Package Management
export const getAdminSmsPackages = () => api.get('/admin/sms-packages');
export const createSmsPackage = (data) => api.post('/admin/sms-packages', data);
export const updateSmsPackage = (id, data) => api.put(`/admin/sms-packages/${id}`, data);
export const deleteSmsPackage = (id) => api.delete(`/admin/sms-packages/${id}`);
export const getUsers = () => api.get('/admin/users');
export const createUser = (data) => api.post('/admin/users', data);
export const deleteUser = (id) => api.delete(`/admin/users/${id}`);
export const getPredefinedOperations = () => api.get('/admin/predefined-operations');
export const createPredefinedOperation = (data) => api.post('/admin/predefined-operations', data);
export const deletePredefinedOperation = (id) => api.delete(`/admin/predefined-operations/${id}`);

// Tickets
export const getTickets = () => api.get('/tickets');
export const getTicket = (id) => api.get(`/tickets/${id}`);
export const createTicket = (data) => api.post('/tickets', data);
export const updateTicket = (id, data) => api.put(`/tickets/${id}`, data);
export const addTicketMessage = (id, message) => api.post(`/tickets/${id}/messages`, { message });
export const getUnreadTicketCount = () => api.get('/tickets/unread/count');

// Customer Auth
export const customerLogin = (phone, password) => api.post('/customer/login', { phone, password });
export const getCustomerProfile = () => api.get('/customer/me');
export const getCustomerPets = () => api.get('/customer/pets');
export const getCustomerOperations = () => api.get('/customer/operations');
export const getCustomerUpcoming = () => api.get('/customer/upcoming');
export const getCustomerClinicInfo = () => api.get('/customer/clinic-info');
export const getCustomerPetWeightHistory = (petId) => api.get(`/customer/pet-weight-history/${petId}`);

// Clinic Stock Management
export const getCategories = () => api.get('/clinic/categories');
export const createCategory = (data) => api.post('/clinic/categories', data);
export const deleteCategory = (id) => api.delete(`/clinic/categories/${id}`);

export const getProducts = () => api.get('/clinic/products');
export const createProduct = (data) => api.post('/clinic/products', data);
export const updateProduct = (id, data) => api.put(`/clinic/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/clinic/products/${id}`);
export const quickStockUpdate = (id, amount) => api.patch(`/clinic/products/${id}/stock`, { amount });
export const batchStockUpdate = (items) => api.post('/clinic/products/batch-stock-update', { items });

export const getClinicOperations = () => api.get('/clinic/clinic-operations');
export const createClinicOperation = (data) => api.post('/clinic/clinic-operations', data);
export const updateClinicOperation = (id, data) => api.put(`/clinic/clinic-operations/${id}`, data);
export const deleteClinicOperation = (id) => api.delete(`/clinic/clinic-operations/${id}`);

export const getStockAlerts = () => api.get('/clinic/stock-alerts');

// NetGSM SMS
export const getNetgsmSettings = () => api.get('/netgsm/settings');
export const updateNetgsmSettings = (data) => api.put('/netgsm/settings', data);
export const sendTestNetgsm = (data) => api.post('/netgsm/test', data);
export const bulkSendSms = (data) => api.post('/netgsm/bulk-send', data);
export const getNetgsmBalance = () => api.get('/netgsm/balance');
export const getNetgsmHeaders = () => api.get('/netgsm/headers');

// SMS Settings
export const getSmsSettings = () => api.get('/settings/sms');
export const updateSmsSettings = (data) => api.put('/settings/sms', data);
export const getCustomerSmsPreference = (id) => api.get(`/customers/${id}/sms-preference`);
export const updateCustomerSmsPreference = (id, data) => api.put(`/customers/${id}/sms-preference`, data);

// SMS History
export const getSmsHistory = (params) => api.get('/sms-history', { params });
export const getSmsStats = () => api.get('/sms-history/stats');

// Manual SMS
export const sendManualSms = (data) => api.post('/sms/send-manual', data);

// Notifications
export const getNotifications = () => api.get('/notifications');
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const clearNotifications = () => api.delete('/notifications');
export const sendCustomNotification = (data) => api.post('/admin/notifications', data);

// Demo
export const createDemoRequest = (data) => api.post('/demo-request', data);
export const getDemoRequests = () => api.get('/demo-requests');
export const approveDemoRequest = (id) => api.post(`/admin/demo-requests/${id}/approve`);
export const rejectDemoRequest = (id) => api.post(`/admin/demo-requests/${id}/reject`);
export const deleteDemoRequest = (id) => api.delete(`/admin/demo-requests/${id}`);
export const getDemoNotificationSettings = () => api.get('/admin/demo-notification-settings');
export const updateDemoNotificationSettings = (data) => api.put('/admin/demo-notification-settings', data);

// Health
// PayTR Settings
export const getPaytrSettings = () => api.get('/paytr/settings');
export const updatePaytrSettings = (data) => api.put('/paytr/settings', data);
export const getPaytrStatus = () => api.get('/paytr/status');
export const createPaytrToken = (orderId) => api.post('/paytr/create-token', { order_id: orderId });
export const getOrderStatus = (orderId) => api.get(`/paytr/order-status/${orderId}`);

// Admin Clinic Management
export const updateClinic = (id, data) => api.put(`/admin/clinics/${id}`, data);
export const deleteClinic = (id) => api.delete(`/admin/clinics/${id}`);

// Admin User Management
export const updateUser = (id, data) => api.put(`/admin/users/${id}`, data);

// Admin Revenue & Orders
export const getAdminRevenue = (period) => api.get(`/admin/revenue?period=${period || 'monthly'}`);
export const getAdminOrders = (params) => {
  const q = new URLSearchParams();
  if (params?.status) q.append('status', params.status);
  if (params?.order_type) q.append('order_type', params.order_type);
  if (params?.clinic_id) q.append('clinic_id', params.clinic_id);
  return api.get(`/admin/orders?${q.toString()}`);
};
export const deleteAdminOrder = (orderId) => api.delete(`/admin/orders/${orderId}`);

export const healthCheck = () => api.get('/health');

// Parasut Integration
export const getParasutSettings = () => api.get('/clinic/parasut-settings');
export const updateParasutSettings = (data) => api.put('/clinic/parasut-settings', data);
export const testParasutConnection = () => api.post('/parasut/test-connection');
export const createParasutInvoice = (data) => api.post('/parasut/create-invoice', data);
export const createBatchInvoice = (data) => api.post('/operations/batch-invoice', data);

// Payment Details
export const getPaymentDetails = (paymentType, date, staffId, dateFrom, dateTo) => {
  const params = new URLSearchParams({ payment_type: paymentType });
  if (dateFrom && dateTo) {
    params.append('date_from', dateFrom);
    params.append('date_to', dateTo);
  } else if (date) {
    params.append('date', date);
  }
  if (staffId) params.append('staff_id', staffId);
  return api.get(`/stats/payment-details?${params.toString()}`);
};

export default api;

// Appointments
export const getAppointments = (startDate, endDate, status) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (status) params.append('status', status);
  return api.get(`/appointments?${params.toString()}`);
};
export const createAppointment = (data) => api.post('/appointments', data);
export const updateAppointmentStatus = (id, status) => api.put(`/appointments/${id}/status?status=${status}`);
export const deleteAppointment = (id) => api.delete(`/appointments/${id}`);

// Calendar
export const getCalendarVaccines = (startDate, endDate) =>
  api.get(`/calendar/vaccines?start_date=${startDate}&end_date=${endDate}`);
export const rescheduleVaccine = (operationId, date) =>
  api.put(`/calendar/vaccines/${operationId}/reschedule?date=${date}`);
export const rescheduleAppointment = (id, date, time) => {
  const params = new URLSearchParams({ date });
  if (time) params.append('time', time);
  return api.put(`/appointments/${id}/reschedule?${params.toString()}`);
};
export const changeVaccineStatus = (operationId, status) =>
  api.put(`/calendar/vaccines/${operationId}/change-status?status=${status}`);

export const getStatusLogs = (limit = 50) =>
  api.get(`/calendar/status-logs?limit=${limit}`);

// Appointment Settings
export const getAppointmentSettings = () => api.get('/settings/appointment');
export const updateAppointmentSettings = (data) => api.put('/settings/appointment', data);

// Booking System
export const getAvailableSlots = (date, operationId) => {
  const params = new URLSearchParams({ date });
  if (operationId) params.append('operation_id', operationId);
  return api.get(`/booking/available-slots?${params.toString()}`);
};
export const getBookingOperations = () => api.get('/booking/clinic-operations');
export const createBookingRequest = (data) => api.post('/booking/request', data);
export const getMyAppointments = () => api.get('/booking/my-appointments');
export const requestCancellation = (bookingId) => api.put(`/booking/my-appointments/${bookingId}/cancel-request`);
export const getBookingRequests = () => api.get('/booking/requests');
export const getBookingRequestsCount = () => api.get('/booking/requests/count');
export const approveBookingRequest = (bookingId) => api.put(`/booking/requests/${bookingId}/approve`);
export const rejectBookingRequest = (bookingId) => api.put(`/booking/requests/${bookingId}/reject`);
export const createDirectBooking = (data) => api.post('/booking/direct', data);
export const getBookingCalendar = (startDate, endDate) =>
  api.get(`/booking/calendar?start_date=${startDate}&end_date=${endDate}`);
export const changeBookingTreatmentStatus = (bookingId, status) =>
  api.put(`/booking/${bookingId}/change-status`, { status });
export const rescheduleBooking = (bookingId, newDate, newTime) =>
  api.put(`/booking/${bookingId}/reschedule`, { new_date: newDate, new_time: newTime });

// Boarding
export const getBoardingRooms = () => api.get('/boarding/rooms');
export const createBoardingRoom = (data) => api.post('/boarding/rooms', data);
export const updateBoardingRoom = (id, data) => api.put(`/boarding/rooms/${id}`, data);
export const deleteBoardingRoom = (id) => api.delete(`/boarding/rooms/${id}`);
export const createBoardingCage = (data) => api.post('/boarding/cages', data);
export const deleteBoardingCage = (id) => api.delete(`/boarding/cages/${id}`);
export const updateBoardingCage = (id, data) => api.put(`/boarding/cages/${id}`, data);
export const getBoardingRecords = (status) => api.get('/boarding/records', { params: { status } });
export const boardingCheckIn = (data) => api.post('/boarding/check-in', data);
export const boardingCheckOut = (id, data) => api.post(`/boarding/check-out/${id}`, data);
export const deleteBoardingRecord = (id) => api.delete(`/boarding/records/${id}`);
export const getBoardingLogs = (limit = 50) => api.get('/boarding/logs', { params: { limit } });
export const getActivityLogs = (category, limit = 100) => api.get('/activity-logs', { params: { category, limit } });
export const getAllLogs = (limit = 200) => api.get('/all-logs', { params: { limit } });

// Pet Notes
export const getPetNotes = (petId) => api.get(`/pets/${petId}/notes`);
export const addPetNote = (petId, formData) => api.post(`/pets/${petId}/notes`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deletePetNote = (petId, noteId) => api.delete(`/pets/${petId}/notes/${noteId}`);

// Pet Lab Results
export const getPetLabResults = (petId, labType) => api.get(`/pets/${petId}/lab-results`, { params: labType ? { lab_type: labType } : {} });
export const addPetLabResult = (petId, formData) => api.post(`/pets/${petId}/lab-results`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deletePetLabResult = (petId, resultId) => api.delete(`/pets/${petId}/lab-results/${resultId}`);
export const getLabTypes = () => api.get('/lab-types');

// Pet Weight History
export const getPetWeightHistory = (petId) => api.get(`/pets/${petId}/weight-history`);
export const addPetWeightRecord = (petId, data) => api.post(`/pets/${petId}/weight-history`, data);
export const deletePetWeightRecord = (petId, recordId) => api.delete(`/pets/${petId}/weight-history/${recordId}`);

// File serving
export const getFileUrl = (path, token) => `${API_URL}/files/${path}?auth=${token}`;
export const downloadFile = (path) => api.get(`/files/${path}`, { responseType: 'blob' });

// Reports
export const getBoardingRevenue = (dateFrom, dateTo, staff) => api.get('/reports/boarding-revenue', { params: { date_from: dateFrom, date_to: dateTo, staff: staff || undefined } });
export const getCustomerBalances = () => api.get('/reports/customer-balances');
export const getPatientStats = () => api.get('/reports/patient-stats');
export const getTopCustomers = (dateFrom, dateTo) => api.get('/reports/top-customers', { params: { date_from: dateFrom, date_to: dateTo } });
export const getOperationalStats = (dateFrom, dateTo) => api.get('/reports/operational', { params: { date_from: dateFrom, date_to: dateTo } });
export const getStockSummary = () => api.get('/reports/stock-summary');
export const getTrends = () => api.get('/reports/trends');
